import React, { useContext, useEffect, useState } from 'react';
import Cross from '../../../../../Assets/Modals/closeIcon.svg';
import { useDispatch, useSelector } from 'react-redux';
import { clearImages, closeModal, selectTagListProduct, toggleTagList, closeTagList, pushToFeedPosts } from '../../../../../Features/lensSlice';
import Tag from '../../../../../Assets/Lens/tagIcon.svg';
import Discard from '../../../../../Assets/Lens/Discard.svg';
import TagList from '../../TagList';
import { ImageContext } from '../../../../../Context/ImagesContext';
import { getMediaUrl, loadImage } from '../../../../../lib/utils/utils';
import { createPost } from '../../../../../lib/utils/apiCalls';
import { showToast } from '../../../../../Components/Toaster/Toaster';
import { v4 as uuidv4 } from 'uuid';
import imageCompression from 'browser-image-compression';

function SingleImageForm() {
    const dispatch = useDispatch();

    const { imagesList, setPostingImagesLoading } = useContext(ImageContext);

    const taggedProduct = useSelector((state) => state.lens.tagList.product);

    const defaultFormErrors = {
        title: '',
        description: '',
    };

    const [formErrors, setFormErrors] = useState(defaultFormErrors);

    const defaultFormFields = {
        title: '',
        description: '',
    };

    const [formFields, setFormFields] = useState(defaultFormFields);

    const handleCloseModal = () => {
        dispatch(closeModal());
        dispatch(clearImages());
        dispatch(selectTagListProduct(taggedProduct));
        dispatch(closeTagList());
    }

    const handleTagButton = () => {
        dispatch(toggleTagList());
    }

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormFields({...formFields, [name]: value});
    }

    const handleRemoveTaggedProduct = (e) => {
        e.preventDefault();
        e.stopPropagation();
        dispatch(selectTagListProduct(taggedProduct));
    }

    const handlePublishPost = async () => {
        const validationErrors = {};

        if(!formFields.title.trim()) {
            validationErrors.title = 'Please enter a title';
        }

        if(!formFields.description.trim()) {
            validationErrors.description = 'Please enter a caption';
        }
        else if(formFields.description.trim().length > 255) {
            validationErrors.description = 'Max 255 characters';
        }

        if(Object.keys(validationErrors).length === 0) {
            setPostingImagesLoading(true);
            setFormErrors(defaultFormErrors);

            let compressedImageList = [];
            const imageUrls = [];

            try {
                if(imagesList[0].file.size > 1048576) {
                    try {
                        let compressedBlob = await imageCompression(imagesList[0].file, {
                            maxSizeMB: 1,
                            maxWidthOrHeight: 1920,
                            useWebWorker: true
                        });

                        let compressedFile = new File([compressedBlob], imagesList[0].file.name, { type: imagesList[0].file.type, lastModified: Date.now() });

                        if (compressedFile) {
                            const img = await loadImage(compressedFile);

                            compressedImageList.push({
                                id: uuidv4(),
                                url: img.src,
                                file: compressedFile
                            });
                        }
                    }
                    catch(err) {
                        console.log(err);
                    }
                }
                else {
                    compressedImageList.push({
                        id: uuidv4(),
                        url: URL.createObjectURL(imagesList[0].file),
                        file: imagesList[0].file,
                    });
                }
            }
            catch(err) {
                console.log(err);
            }

            const url = await getMediaUrl(compressedImageList[0].file);
            imageUrls.push(url);

            try {
                const postData = {
                    postTitle: formFields.title,
                    postDesc: formFields.description,
                    imageUrls: imageUrls,
                };

                if (taggedProduct.id !== null) {
                    postData.productId = taggedProduct.id;
                }

                const response = await createPost(postData);

                if (response?.status === 200) {
                    handleCloseModal();
                    setPostingImagesLoading(false);
                    dispatch(pushToFeedPosts(response.data.data));
                    showToast('Your post is live.', 'success');
                }
            }
            catch (err) {
                showToast('Something went wrong while uploading your photo.', 'error');
            }
        }
        else {
            setFormErrors(validationErrors);
            return;
        }
    }

    useEffect(() => {
        if(taggedProduct.id) {
            const validationErrors = {};

            validationErrors.title = formErrors.title;
            validationErrors.description = formErrors.description;
            validationErrors.taggedProduct = '';

            setFormErrors(validationErrors);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [taggedProduct.id]);

    return (
        <div className="w-2/5 relative flex flex-col h-full p-4">
            <div className="flex items-center justify-between">
                <h1 className='font-semibold text-2xl text-[#121212]'>Default Post</h1>

                <button onClick={handleCloseModal} className="w-11 h-11 flex items-center justify-center">
                    <img src={Cross} alt="" />
                </button>
            </div>

            <TagList />

            <div className="flex flex-col gap-4 mt-4 pb-16 overflow-y-scroll no-scrollbar">
                <div className="flex flex-col gap-2">
                    <div className="flex items-center justify-between">
                        <label className='font-medium text-base text-[#121212]' htmlFor="title">Post Title</label>

                        {formErrors.title && <p className='text-[14px] leading-5 text-[#DC2626]'>{formErrors.title}</p>}
                    </div>

                    <input 
                        value={formFields.title} 
                        onChange={handleInputChange} 
                        className='outline-none text-[#121212] px-3 py-4 font-normal text-base placeholder:text-[#666666] bg-[#F5F5F5] rounded-[9px] border border-[#EAEAEA] focus:border-primaryColor' 
                        id='title' 
                        placeholder='Add title' 
                        type="text" 
                        name='title' 
                    />
                </div>

                <div className="flex flex-col gap-2">
                    <div className="flex items-center justify-between">
                        <label className='font-medium text-base text-[#121212]' htmlFor="title">Post Description <span className='text-[14px] leading-5 text-[#808080]'>&#40;{formFields.description.length}/255&#41;</span></label>

                        {formErrors.description && <p className='text-[14px] leading-5 text-[#DC2626]'>{formErrors.description}</p>}
                    </div>

                    <textarea 
                        value={formFields.description} 
                        onChange={handleInputChange} 
                        className='h-[240px] outline-none text-[#121212] px-3 py-4 font-normal text-base placeholder:text-[#666666] bg-[#F5F5F5] rounded-[9px] border border-[#EAEAEA] focus:border-primaryColor' 
                        id='description' 
                        placeholder='Write a short description about your brand, product quality...' 
                        type="text" 
                        name='description' 
                    />
                </div>

                <div onClick={handleTagButton} className='bg-[#F5F5F5] cursor-pointer rounded-[9px] p-3 flex items-center gap-1'>
                    <div className="w-6 h-6 flex items-center justify-center">
                        <img src={Tag} alt="" />
                    </div>

                    {
                        taggedProduct.id ? 
                        <p className='w-[calc(100%-48px)] select-none font-normal text-[12px] leading-6 text-[#121212] text-left'>{taggedProduct.name}</p> : 
                        <p className='font-normal text-base select-none text-[#666666]'>tag product</p>
                    }

                    {
                        taggedProduct.id &&
                        <button onClick={handleRemoveTaggedProduct} className="ml-auto w-6 h-6 flex items-center justify-center">
                            <img src={Discard} alt="" />
                        </button>
                    }
                </div>
            </div>

            <div className="absolute bottom-0 left-0 w-full px-4 py-3 bg-[#FFFFFF] border-t border-[#EAEAEA]">
                <button onClick={handlePublishPost} className='w-full px-4 py-[6px] rounded-md bg-primaryColor hover:bg-primaryAccent font-semibold text-[14px] leading-[26px] text-buttonText'>Publish Post</button>
            </div>
        </div>
    )
}

export default SingleImageForm;
